import React, {
	Suspense,
	lazy,
	useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
	Alert,
	Box,
	CircularProgress,
} from '@mui/material';

import QuillesSiteFooter from '../components/QuillesSiteFooter';
import QuillesSponsorBanner from '../components/QuillesSponsorBanner';
import CompetitionFiltersToolbar from '../components/newResults/CompetitionFiltersToolbar';
import EquipesTabToolbar, { JoueursTabToolbar } from '../components/newResults/EquipesTabToolbar';
import PlayersRankingTable from '../components/newResults/PlayersRankingTable';
import PlayerStatsModal from '../components/newResults/PlayerStatsModal';
import { usePlayerCareerData } from '../hooks/usePlayerCareerData';
import { useQuillesData } from '../hooks/useQuillesData';
import { useSponsorBanner } from '../hooks/useSponsorBanner';
import {
	COMPETITION_URL_PARAM_KEYS,
	EQUIPES_FILTERS_STORAGE_KEY,
	EQUIPES_URL_PARAM_KEYS,
	FAVORITES_STORAGE_KEY,
	JOUEURS_FILTERS_STORAGE_KEY,
	JOUEURS_URL_PARAM_KEYS,
	MAIN_TABS,
} from './newResults/newResultsConstants';
import {
	applyEquipesParamUpdates,
	applyJoueursParamUpdates,
	buildPageState,
	equipesFiltersFromLegacyUrl,
	equipesFiltersFromStorage,
	hasEquipesUrlParams,
	hasJoueursUrlParams,
	joueursFiltersFromLegacyUrl,
	joueursFiltersFromStorage,
	mergeEquipesFiltersWithLegacyUrl,
	mergeJoueursFiltersWithLegacyUrl,
	pickUrlParamUpdates,
	readEquipesFilters,
	readFavoriteTeams,
	readJoueursFilters,
	stripLocalFilterUrlParams,
	tabSwitchNavigationResets,
	validateCompetitionParams,
} from './newResults/newResultsNavigation';
import { OFFICIAL_TEAM_RESULTS_URL } from '../services/quillesConstants';
import {
	buildCareerPlayerCatalog,
	getAvailableRounds,
	getCategories,
	getChampionships,
	getClubs,
	getPlayerCareer,
	getPlayerCareerStats,
	filterPlayerCareer,
	filterPlayerCareerStats,
	isIndividuelChampionship,
	getPlayerDetail,
	getTeamDetail,
	getTeamRankings,
	getTeamRankingsByCategory,
	getTeamRankingsForRound,
	getTopPlayers,
	getZones,
	isEquipesTeamRow,
} from '../services/csvDataService';
import { resolveModernResultsPageTitle } from '../services/analyticsPageTitles';
import { trackAnalyticsPageView } from '../services/analyticsPageView';
import NewResultsThemeToggle from '../components/newResults/NewResultsThemeToggle';
import { NewResultsThemeProvider } from '../hooks/useNewResultsTheme';
import {
	PLAYER_SORT_AVERAGE,
	resolveAutoTeamSortColumn,
	sortPlayerRows,
	sortTeamRankingRows,
	TEAM_SORT_TOTAL,
} from '../utils/newResultsTeamSort';
import '../styles/newResults.css';

const TeamsRankingsPanel = lazy(() => import('../components/newResults/TeamsRankingsPanel'));
const CareerTabPanel = lazy(() => import('../components/newResults/CareerTabPanel'));
const PlayerCareerDetailPage = lazy(() => import('../components/newResults/PlayerCareerDetailPage'));
const PlayerCareerStatsPage = lazy(() => import('../components/newResults/PlayerCareerStatsPage'));

function LazyPanelFallback() {
	return (
		<div className="new-results-loading new-results-loading--inline">
			<CircularProgress size={28} />
		</div>
	);
}

function NewResultatsEquipes() {
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [joueursFilters, setJoueursFilters] = useState(() => (
		joueursFiltersFromStorage(readJoueursFilters())
	));
	const [equipesFilters, setEquipesFilters] = useState(() => (
		equipesFiltersFromStorage(readEquipesFilters())
	));
	const state = useMemo(
		() => buildPageState(searchParams, joueursFilters, equipesFilters),
		[searchParams, joueursFilters, equipesFilters],
	);
	const { data, loading, error } = useQuillesData(state.year);
	const isCareerTab = state.mainTab === 'carriere';
	const {
		seasonDataByYear,
		individualsByYear,
		loading: careerLoading,
		error: careerError,
	} = usePlayerCareerData(isCareerTab);
	const [localSearch, setLocalSearch] = useState(state.searchQuery);
	const [favoriteTeams, setFavoriteTeams] = useState(readFavoriteTeams);
	const [teamSortColumn, setTeamSortColumn] = useState(TEAM_SORT_TOTAL);
	const [playerSortColumn, setPlayerSortColumn] = useState(PLAYER_SORT_AVERAGE);
	const urlFilterMigratedRef = useRef(false);
	const hasStoredPlayerClubsPreferenceRef = useRef(false);
	const teamSortManualRef = useRef(false);
	const teamSortContextKeyRef = useRef('');

	useEffect(() => {
		setLocalSearch(state.searchQuery);
	}, [state.searchQuery]);

	useEffect(() => {
		if (typeof window === 'undefined') return;
		window.localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favoriteTeams));
	}, [favoriteTeams]);

	const zones = useMemo(
		() => (data ? getZones(data.teams, data.individuals) : []),
		[data],
	);
	const championships = useMemo(
		() => (data && state.zone
			? getChampionships(data.teams, state.zone, data.individuals)
			: []),
		[data, state.zone],
	);
	const categories = useMemo(
		() => (data && state.zone && state.championship
			? getCategories(data.teams, state.zone, state.championship, data.individuals)
			: []),
		[data, state.zone, state.championship],
	);
	const equipesChampionships = useMemo(() => {
		if (!data) return [];
		return [...new Set(
			data.teams
				.filter((row) => {
					if (!isEquipesTeamRow(row)) return false;
					if (state.equipesZone && row.zone !== state.equipesZone) return false;
					return Boolean(row.championship);
				})
				.map((row) => row.championship),
		)].sort((a, b) => a.localeCompare(b, 'fr'));
	}, [data, state.equipesZone]);
	const clubs = useMemo(() => {
		if (!data) return [];
		return getClubs(
			data.teams.filter(isEquipesTeamRow),
			{
				zone: state.equipesZone || undefined,
				championship: state.equipesCategory || undefined,
			},
		);
	}, [data, state.equipesZone, state.equipesCategory]);

	const filters = useMemo(() => ({
		year: state.year,
		zone: state.zone,
		championship: state.championship,
		category: state.category,
	}), [state]);

	const hasCompetitionContext = Boolean(state.zone && state.championship && state.category);

	const isIndividuelCompetition = useMemo(
		() => (data && hasCompetitionContext
			? isIndividuelChampionship(
				data.teams,
				state.zone,
				state.championship,
				data.individuals,
			)
			: false),
		[data, hasCompetitionContext, state.zone, state.championship],
	);

	const rounds = useMemo(
		() => (data && hasCompetitionContext
			? getAvailableRounds(data.teams, filters, data.individuals)
			: []),
		[data, filters, hasCompetitionContext],
	);

	const activeRound = useMemo(() => {
		if (state.round && rounds.includes(state.round)) return state.round;
		return rounds[rounds.length - 1] || '';
	}, [state.round, rounds]);

	const sponsorRedrawKey = useMemo(() => {
		if (state.mainTab === 'carriere' && state.playerLicence) {
			if (state.careerStats) return `carriere:stats:${state.playerLicence}`;
			return `carriere:${state.playerLicence}`;
		}
		if (state.subView === 'manches') {
			return `manches:${activeRound}`;
		}
		return state.subView;
	}, [state.mainTab, state.playerLicence, state.careerStats, state.subView, activeRound]);
	const sponsorBanner = useSponsorBanner(sponsorRedrawKey);

	const patchParams = useCallback((updates) => {
		const hasJoueursUpdates = Object.keys(updates).some(
			(key) => JOUEURS_URL_PARAM_KEYS.includes(key),
		);
		if (hasJoueursUpdates) {
			setJoueursFilters((prev) => {
				const next = applyJoueursParamUpdates(prev, updates);
				if (updates.q !== undefined) {
					setLocalSearch(next.q);
				}
				return next;
			});
		}

		const hasEquipesUpdates = Object.keys(updates).some(
			(key) => EQUIPES_URL_PARAM_KEYS.includes(key),
		);
		if (hasEquipesUpdates) {
			setEquipesFilters((prev) => applyEquipesParamUpdates(prev, updates));
		}

		const urlUpdates = pickUrlParamUpdates(updates);
		if (Object.keys(urlUpdates).length === 0) return;

		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			Object.entries(urlUpdates).forEach(([key, value]) => {
				if (value === null || value === undefined || value === '') next.delete(key);
				else next.set(key, String(value));
			});
			return next;
		}, { replace: true });
	}, [setSearchParams]);

	useEffect(() => {
		if (urlFilterMigratedRef.current) return;
		urlFilterMigratedRef.current = true;

		const savedJoueurs = readJoueursFilters();
		hasStoredPlayerClubsPreferenceRef.current = Boolean(savedJoueurs?.hasPclubsKey);

		const hasJoueursLegacy = hasJoueursUrlParams(searchParams);
		const hasEquipesLegacy = hasEquipesUrlParams(searchParams);
		const savedEquipes = readEquipesFilters();

		if (hasJoueursLegacy) {
			const legacy = joueursFiltersFromLegacyUrl(searchParams);
			setJoueursFilters((prev) => mergeJoueursFiltersWithLegacyUrl(prev, legacy, searchParams));
			if (legacy.q) setLocalSearch(legacy.q);
		}
		if (hasEquipesLegacy) {
			const legacy = equipesFiltersFromLegacyUrl(searchParams);
			setEquipesFilters((prev) => mergeEquipesFiltersWithLegacyUrl(prev, legacy));
		}

		const urlUpdates = {};
		if (!searchParams.get('year') && savedEquipes?.year) {
			urlUpdates.year = savedEquipes.year;
		}

		if (hasJoueursLegacy || hasEquipesLegacy) {
			setSearchParams((prev) => {
				let next = stripLocalFilterUrlParams(prev);
				Object.entries(urlUpdates).forEach(([key, value]) => {
					next.set(key, String(value));
				});
				return next;
			}, { replace: true });
		} else if (Object.keys(urlUpdates).length > 0) {
			patchParams(urlUpdates);
		}
	}, [searchParams, setSearchParams, patchParams]);

	useEffect(() => {
		if (typeof window === 'undefined') return;
		window.localStorage.setItem(EQUIPES_FILTERS_STORAGE_KEY, JSON.stringify({
			year: state.year,
			eqZone: equipesFilters.eqZone,
			cat: equipesFilters.cat,
			club: equipesFilters.club,
		}));
	}, [state.year, equipesFilters]);

	useEffect(() => {
		if (typeof window === 'undefined') return;
		window.localStorage.setItem(JOUEURS_FILTERS_STORAGE_KEY, JSON.stringify({
			licTypes: joueursFilters.licTypes,
			licCats: joueursFilters.licCats,
			pclubs: joueursFilters.pclubs,
			minManches: joueursFilters.minManches,
			psize: joueursFilters.psize,
			q: joueursFilters.q,
		}));
	}, [joueursFilters]);

	useEffect(() => {
		const keysToStrip = [];
		if (state.mainTab === 'joueurs' || state.mainTab === 'equipes' || state.mainTab === 'carriere') {
			keysToStrip.push(...COMPETITION_URL_PARAM_KEYS);
		}
		if (state.mainTab === 'competitions' || state.mainTab === 'joueurs' || state.mainTab === 'carriere') {
			keysToStrip.push(...EQUIPES_URL_PARAM_KEYS);
		}
		if (state.mainTab !== 'joueurs' && state.mainTab !== 'carriere') {
			keysToStrip.push(...JOUEURS_URL_PARAM_KEYS);
		}
		if (state.mainTab !== 'carriere' || !state.playerLicence) {
			keysToStrip.push('stats', 'cfrom', 'cto', 'csenior');
		}
		const orphanKeys = [...new Set(keysToStrip)].filter((key) => searchParams.has(key));
		if (!orphanKeys.length) return;

		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			orphanKeys.forEach((key) => next.delete(key));
			return next;
		}, { replace: true });
	}, [state.mainTab, state.playerLicence, searchParams, setSearchParams]);

	useEffect(() => {
		if (!data || !state.zone || state.championship) return;
		const zoneChampionships = getChampionships(data.teams, state.zone, data.individuals);
		if (zoneChampionships.length === 1) {
			patchParams({ championship: zoneChampionships[0] });
		}
	}, [data, state.zone, state.championship, patchParams]);

	useEffect(() => {
		if (!data || state.mainTab !== 'competitions') return;
		if (!state.zone && !state.championship && !state.category && !state.round) return;

		const validated = validateCompetitionParams(data, {
			zone: state.zone,
			championship: state.championship,
			category: state.category,
			round: state.round,
		});

		const updates = {};
		if (validated.zone !== (state.zone || null)) updates.zone = validated.zone;
		if (validated.championship !== (state.championship || null)) {
			updates.championship = validated.championship;
		}
		if (validated.category !== (state.category || null)) updates.category = validated.category;
		if (validated.round !== (state.round || null)) updates.round = validated.round;

		const contextChanged = ['zone', 'championship', 'category', 'round'].some(
			(key) => Object.prototype.hasOwnProperty.call(updates, key),
		);
		if (contextChanged && state.team) updates.team = null;

		if (Object.keys(updates).length > 0) {
			patchParams(updates);
		}
	}, [
		data,
		state.mainTab,
		state.zone,
		state.championship,
		state.category,
		state.round,
		state.team,
		patchParams,
	]);

	const rankings = useMemo(() => {
		if (!data || !hasCompetitionContext) return [];
		return getTeamRankings(data.teams, data.individuals, filters);
	}, [data, filters, hasCompetitionContext]);

	const roundRankings = useMemo(() => {
		if (!data || !hasCompetitionContext || !activeRound) return [];
		return getTeamRankingsForRound(data.teams, data.individuals, filters, activeRound);
	}, [data, filters, hasCompetitionContext, activeRound]);

	const equipesRankings = useMemo(() => {
		if (!data || (!state.equipesCategory && !state.club)) return [];
		return getTeamRankingsByCategory(data.teams, {
			zone: state.equipesZone || '',
			championship: state.equipesCategory,
			club: state.club,
		});
	}, [data, state.equipesCategory, state.equipesZone, state.club]);
	const equipesRankingsWithChampionshipRank = useMemo(() => {
		if (!data || !equipesRankings.length) return equipesRankings;
		const contextCache = new Map();
		return equipesRankings.map((row) => {
			const key = `${row.zone}::${row.championship}::${row.category}`;
			if (!contextCache.has(key)) {
				const contextRankings = getTeamRankings(data.teams, data.individuals, {
					year: state.year,
					zone: row.zone,
					championship: row.championship,
					category: row.category,
				});
				contextCache.set(key, {
					ranks: new Map(contextRankings.map((entry) => [entry.teamNumber, entry.rank])),
					total: contextRankings.length,
				});
			}
			const context = contextCache.get(key);
			return {
				...row,
				championshipRank: context.ranks.get(row.teamNumber) ?? null,
				championshipTeamsTotal: context.total,
			};
		});
	}, [data, equipesRankings, state.year]);

	const topPlayers = useMemo(() => {
		if (!data || !hasCompetitionContext) return [];
		const useRound = state.subView === 'manches' && activeRound;
		return getTopPlayers(data.individuals, filters, {
			round: useRound ? activeRound : null,
			limit: 20,
			allZones: true,
		});
	}, [data, filters, hasCompetitionContext, state.subView, activeRound]);

	const competitionPlayers = useMemo(() => {
		if (!data || !hasCompetitionContext) return [];
		return getTopPlayers(data.individuals, filters, { limit: null });
	}, [data, filters, hasCompetitionContext]);

	/** Championnat INDIVIDUEL : classement joueurs (saison ou manche), pas agrégat équipe. */
	const competitionMainPlayers = useMemo(() => {
		if (!data || !hasCompetitionContext || !isIndividuelCompetition) return [];
		const useRound = state.subView === 'manches' && activeRound;
		return getTopPlayers(data.individuals, filters, {
			round: useRound ? activeRound : null,
			limit: null,
		});
	}, [
		data,
		filters,
		hasCompetitionContext,
		isIndividuelCompetition,
		state.subView,
		activeRound,
	]);

	const categoryPlayers = useMemo(() => {
		if (!data) return [];
		const grouped = new Map();
		data.individuals.forEach((row) => {
			const key = row.licence;
			if (!grouped.has(key)) {
				grouped.set(key, {
					licence: row.licence,
					playerName: row.playerName,
					club: row.club,
					licenceLevel: row.licenceLevel,
					licenceCategory: row.licenceCategory,
					teamName: row.teamName,
					teamNumber: row.teamNumber,
					manches: 0,
					quilles: 0,
					parties: 0,
				});
			}
			const entry = grouped.get(key);
			entry.manches += 1;
			entry.quilles += Number(row.quilles);
			entry.parties += Number(row.parties);
		});
		return [...grouped.values()].map((p) => ({
			...p,
			average: p.parties > 0 ? p.quilles / p.parties : 0,
		}));
	}, [data]);
	const playerLicenceTypes = useMemo(() => [...new Set(
		categoryPlayers
			.map((p) => p.licenceLevel)
			.filter(Boolean),
	)].sort((a, b) => a.localeCompare(b, 'fr')), [categoryPlayers]);
	const playerLicenceCategories = useMemo(() => [...new Set(
		categoryPlayers
			.map((p) => p.licenceCategory)
			.filter(Boolean),
	)].sort((a, b) => a.localeCompare(b, 'fr')), [categoryPlayers]);
	const playerClubs = useMemo(() => [...new Set(
		categoryPlayers
			.map((p) => p.club)
			.filter(Boolean),
	)].sort((a, b) => a.localeCompare(b, 'fr')), [categoryPlayers]);
	useEffect(() => {
		if (state.mainTab !== 'joueurs') return;
		if (hasStoredPlayerClubsPreferenceRef.current) return;
		if (state.playerClubs.length > 0 || playerClubs.length === 0) return;
		hasStoredPlayerClubsPreferenceRef.current = true;
		setJoueursFilters((prev) => ({
			...prev,
			pclubs: playerClubs,
			page: 1,
		}));
	}, [
		state.mainTab,
		state.playerClubs,
		playerClubs,
	]);

	const playersBeforeMinManches = useMemo(() => {
		const q = localSearch.trim().toLowerCase();
		const qNorm = q.replace(/^0+/, '');
		return categoryPlayers.filter((p) => {
			if (state.playerLicenceTypes.length > 0 && !state.playerLicenceTypes.includes(p.licenceLevel)) return false;
			if (state.playerLicenceCategories.length > 0 && !state.playerLicenceCategories.includes(p.licenceCategory)) return false;
			if (state.playerClubs.length > 0 && !state.playerClubs.includes(p.club)) return false;
			if (q.length < 2) return true;
			const name = p.playerName.toLowerCase();
			const licence = p.licence ?? '';
			const licenceNorm = licence.replace(/^0+/, '');
			return name.includes(q) || licence.includes(q) || licenceNorm.includes(qNorm);
		});
	}, [
		categoryPlayers,
		localSearch,
		state.playerLicenceTypes,
		state.playerLicenceCategories,
		state.playerClubs,
	]);
	const maxManchesInPlayerList = useMemo(
		() => playersBeforeMinManches.reduce((max, player) => Math.max(max, Number(player.manches) || 0), 0),
		[playersBeforeMinManches],
	);
	const minManchesSelected = Number(state.playerMinManches) || 0;
	const maxManchesOption = Math.max(maxManchesInPlayerList, minManchesSelected, 0);
	const minManchesOptions = useMemo(
		() => Array.from({ length: maxManchesOption + 1 }, (_, idx) => idx),
		[maxManchesOption],
	);
	const filteredCategoryPlayers = useMemo(() => {
		if (!Number.isFinite(minManchesSelected) || minManchesSelected <= 0) {
			return playersBeforeMinManches;
		}
		return playersBeforeMinManches.filter((p) => Number(p.manches) >= minManchesSelected);
	}, [playersBeforeMinManches, minManchesSelected]);
	const sortedFilteredCategoryPlayers = useMemo(
		() => sortPlayerRows(filteredCategoryPlayers, playerSortColumn),
		[filteredCategoryPlayers, playerSortColumn],
	);
	const sortedCompetitionPlayers = useMemo(
		() => sortPlayerRows(competitionPlayers, playerSortColumn),
		[competitionPlayers, playerSortColumn],
	);
	const playerRowsPerPage = state.playerPageSize;
	const totalPlayerCount = sortedFilteredCategoryPlayers.length;
	const totalPlayerPages = Math.max(1, Math.ceil(totalPlayerCount / playerRowsPerPage));
	const effectivePlayerPage = Math.min(state.playerPage, totalPlayerPages);
	const playerPageStartIndex = totalPlayerCount > 0
		? (effectivePlayerPage - 1) * playerRowsPerPage
		: 0;
	const pagedCategoryPlayers = useMemo(() => (
		sortedFilteredCategoryPlayers.slice(playerPageStartIndex, playerPageStartIndex + playerRowsPerPage)
	), [sortedFilteredCategoryPlayers, playerPageStartIndex, playerRowsPerPage]);

	const careerPlayers = useMemo(
		() => (individualsByYear ? buildCareerPlayerCatalog(individualsByYear) : []),
		[individualsByYear],
	);
	const filteredCareerPlayers = useMemo(() => {
		const q = localSearch.trim().toLowerCase();
		const qNorm = q.replace(/^0+/, '');
		return careerPlayers.filter((p) => {
			if (q.length < 2) return true;
			const name = p.playerName.toLowerCase();
			const licence = p.licence ?? '';
			const licenceNorm = licence.replace(/^0+/, '');
			return name.includes(q) || licence.includes(q) || licenceNorm.includes(qNorm);
		});
	}, [careerPlayers, localSearch]);
	const sortedFilteredCareerPlayers = useMemo(
		() => sortPlayerRows(filteredCareerPlayers, playerSortColumn),
		[filteredCareerPlayers, playerSortColumn],
	);
	const totalCareerPlayerCount = sortedFilteredCareerPlayers.length;
	const totalCareerPlayerPages = Math.max(1, Math.ceil(totalCareerPlayerCount / playerRowsPerPage));
	const effectiveCareerPlayerPage = Math.min(state.playerPage, totalCareerPlayerPages);
	const careerPlayerPageStartIndex = totalCareerPlayerCount > 0
		? (effectiveCareerPlayerPage - 1) * playerRowsPerPage
		: 0;
	const pagedCareerPlayers = useMemo(() => (
		sortedFilteredCareerPlayers.slice(
			careerPlayerPageStartIndex,
			careerPlayerPageStartIndex + playerRowsPerPage,
		)
	), [sortedFilteredCareerPlayers, careerPlayerPageStartIndex, playerRowsPerPage]);
	const fullCareer = useMemo(() => {
		if (!seasonDataByYear || !state.playerLicence) return null;
		return getPlayerCareer(seasonDataByYear, state.playerLicence);
	}, [seasonDataByYear, state.playerLicence]);
	const fullCareerStats = useMemo(() => {
		if (!seasonDataByYear || !state.playerLicence) return null;
		return getPlayerCareerStats(seasonDataByYear, state.playerLicence);
	}, [seasonDataByYear, state.playerLicence]);
	const careerFilter = useMemo(() => ({
		fromYear: state.careerFromYear && !Number.isNaN(state.careerFromYear) ? state.careerFromYear : null,
		toYear: state.careerToYear && !Number.isNaN(state.careerToYear) ? state.careerToYear : null,
		seniorsOnly: Boolean(state.careerSeniorsOnly),
	}), [state.careerFromYear, state.careerToYear, state.careerSeniorsOnly]);
	const selectedCareer = useMemo(
		() => filterPlayerCareer(fullCareer, careerFilter),
		[fullCareer, careerFilter],
	);
	const selectedCareerStats = useMemo(
		() => filterPlayerCareerStats(fullCareerStats, careerFilter),
		[fullCareerStats, careerFilter],
	);
	const handleCareerFilterChange = useCallback((next) => {
		patchParams({
			cfrom: next.fromYear ?? null,
			cto: next.toYear ?? null,
			csenior: next.seniorsOnly ? '1' : null,
		});
	}, [patchParams]);

	const expandedTeam = useMemo(() => {
		if (!data || !state.team || !hasCompetitionContext) return null;
		return getTeamDetail(data.teams, data.individuals, filters, state.team);
	}, [data, state.team, filters, hasCompetitionContext]);

	const selectedPlayer = useMemo(() => {
		if (!data || !state.playerLicence) return null;
		return getPlayerDetail(data.individuals, state.playerLicence);
	}, [data, state.playerLicence]);
	const currentYearFavorites = useMemo(
		() => favoriteTeams.filter((fav) => Number(fav.year) === Number(state.year)),
		[favoriteTeams, state.year],
	);
	const favoriteIds = useMemo(
		() => new Set(currentYearFavorites.map((fav) => fav.id)),
		[currentYearFavorites],
	);
	const favoriteRows = useMemo(() => {
		if (!currentYearFavorites.length) return [];
		if (!data) {
			return currentYearFavorites.map((fav) => ({
				...fav,
				favoriteId: fav.id,
				quillesN: null,
				totalQuilles: null,
				totalAverage: null,
				championshipRank: null,
				championshipTeamsTotal: null,
				rank: null,
			}));
		}
		const contextCache = new Map();
		return currentYearFavorites.map((fav) => {
			const contextKey = `${fav.zone}::${fav.championship}::${fav.category}`;
			if (!contextCache.has(contextKey)) {
				const contextRankings = getTeamRankings(data.teams, data.individuals, {
					year: state.year,
					zone: fav.zone,
					championship: fav.championship,
					category: fav.category,
				});
				contextCache.set(contextKey, {
					byTeam: new Map(contextRankings.map((entry) => [entry.teamNumber, entry])),
					total: contextRankings.length,
				});
			}
			const { byTeam, total } = contextCache.get(contextKey);
			const live = byTeam.get(fav.teamNumber);
			return {
				...fav,
				favoriteId: fav.id,
				teamName: live?.teamName ?? fav.teamName,
				club: live?.club ?? fav.club,
				quillesN: live?.quillesN ?? null,
				totalQuilles: live?.totalQuilles ?? null,
				totalAverage: live?.totalAverage ?? null,
				championshipRank: live?.rank ?? null,
				championshipTeamsTotal: total,
				rank: null,
			};
		});
	}, [currentYearFavorites, data, state.year]);

	const sortedFavoriteRows = useMemo(() => {
		if (!favoriteRows.length) return favoriteRows;
		return sortTeamRankingRows(favoriteRows, teamSortColumn)
			.map((row, index) => ({ ...row, rank: index + 1 }));
	}, [favoriteRows, teamSortColumn]);

	const heroTitle = useMemo(() => {
		if (state.mainTab === 'carriere' && state.playerLicence) {
			if (state.careerStats) {
				return selectedCareerStats?.playerName
					? `Statistiques · ${selectedCareerStats.playerName}`
					: 'Statistiques carrière';
			}
			return selectedCareer?.playerName ?? 'Carrière joueur';
		}
		if (hasCompetitionContext) {
			return state.championship;
		}
		if (state.mainTab === 'equipes') {
			if (state.equipesCategory && state.club) return `Équipes · ${state.equipesCategory} · ${state.club}`;
			if (state.equipesCategory) return `Équipes · ${state.equipesCategory}`;
			if (state.club) return `Équipes · ${state.club}`;
		}
		return 'Résultats quilles de huit';
	}, [state, hasCompetitionContext, selectedCareer, selectedCareerStats]);

	const heroSub = useMemo(() => {
		if (state.mainTab === 'carriere' && state.playerLicence) {
			if (state.careerStats && selectedCareerStats) {
				const parts = [`Licence ${selectedCareerStats.licence}`];
				if (selectedCareerStats.seasonCount) {
					parts.push(`${selectedCareerStats.seasonCount} saisons · ${selectedCareerStats.totalManches} manches`);
				}
				return parts.join(' · ');
			}
			if (selectedCareer) {
				const parts = [`Licence ${selectedCareer.licence}`];
				if (selectedCareer.club) parts.push(selectedCareer.club);
				if (selectedCareer.seasonCount) parts.push(`${selectedCareer.seasonCount} saisons`);
				return parts.join(' · ');
			}
			return `Licence ${state.playerLicence}`;
		}
		if (hasCompetitionContext) return `${state.zone} · ${state.category}`;
		if (state.mainTab === 'equipes' && state.equipesZone) return state.equipesZone;
		if (state.mainTab === 'joueurs') return `Saison ${state.year} · filtres joueurs`;
		if (state.mainTab === 'carriere') return 'Recherche par nom ou n° de licence';
		if (state.zone) return state.zone;
		return 'Sélectionnez une compétition ou recherchez un joueur';
	}, [state, hasCompetitionContext, selectedCareer, selectedCareerStats]);

	const displayRows = useMemo(() => {
		if (state.mainTab === 'equipes') return equipesRankingsWithChampionshipRank;
		if (state.subView === 'manches') return roundRankings;
		return rankings;
	}, [state.mainTab, state.subView, equipesRankingsWithChampionshipRank, roundRankings, rankings]);

	const teamSortContextKey = useMemo(() => [
		state.mainTab,
		state.subView,
		state.year,
		state.zone,
		state.championship,
		state.category,
		state.equipesZone,
		state.equipesCategory,
		state.club,
		activeRound,
	].join('|'), [
		state.mainTab,
		state.subView,
		state.year,
		state.zone,
		state.championship,
		state.category,
		state.equipesZone,
		state.equipesCategory,
		state.club,
		activeRound,
	]);

	const isRoundTeamTable = state.mainTab === 'competitions' && state.subView === 'manches';

	useEffect(() => {
		if (teamSortContextKeyRef.current !== teamSortContextKey) {
			teamSortContextKeyRef.current = teamSortContextKey;
			teamSortManualRef.current = false;
		}
	}, [teamSortContextKey]);

	useEffect(() => {
		if (teamSortManualRef.current) return;
		const rowsForAutoSort = displayRows.length > 0
			? displayRows
			: (state.mainTab === 'equipes' ? favoriteRows : []);
		if (!rowsForAutoSort.length) return;
		setTeamSortColumn(resolveAutoTeamSortColumn(rowsForAutoSort, { isRoundView: isRoundTeamTable }));
	}, [displayRows, favoriteRows, isRoundTeamTable, teamSortContextKey, state.mainTab]);

	const handleTeamSortColumn = useCallback((column) => {
		teamSortManualRef.current = true;
		setTeamSortColumn(column);
	}, []);

	useEffect(() => {
		setPlayerSortColumn(PLAYER_SORT_AVERAGE);
	}, [
		state.mainTab,
		state.subView,
		state.year,
		state.zone,
		state.championship,
		state.category,
		localSearch,
		state.playerLicenceTypes,
		state.playerLicenceCategories,
		state.playerClubs,
		minManchesSelected,
	]);

	const sortedDisplayRows = useMemo(() => {
		if (!displayRows.length) return displayRows;
		return sortTeamRankingRows(displayRows, teamSortColumn);
	}, [displayRows, teamSortColumn]);

	const teamPreview = useMemo(() => {
		if (!state.team || expandedTeam) return null;
		const row = displayRows.find((r) => String(r.teamNumber) === String(state.team));
		if (!row) return null;
		return {
			teamName: row.teamName,
			category: row.category ?? filters.category,
			club: row.club,
			teamNumber: row.teamNumber,
		};
	}, [state.team, expandedTeam, displayRows, filters.category]);

	useLayoutEffect(() => {
		const pagePath = `${location.pathname}${location.search}`;
		const title = resolveModernResultsPageTitle({
			selectedPlayer: state.mainTab !== 'carriere' ? selectedPlayer : null,
			expandedTeam,
			teamPreview,
			mainTab: state.mainTab,
			selectedCareer: state.mainTab === 'carriere' ? selectedCareer : null,
			competitionContext: state.mainTab === 'competitions' && hasCompetitionContext
				? {
					zone: state.zone,
					championship: state.championship,
					category: state.category,
				}
				: null,
		});
		trackAnalyticsPageView(title, pagePath);
	}, [
		selectedPlayer,
		expandedTeam,
		teamPreview,
		selectedCareer,
		state.mainTab,
		state.zone,
		state.championship,
		state.category,
		hasCompetitionContext,
		location.pathname,
		location.search,
	]);

	const showSidebar = state.mainTab === 'competitions' && hasCompetitionContext
		&& state.subView !== 'joueurs';

	const updateFilter = (key, value) => {
		const resets = {};
		if (key === 'year') {
			// Conserver zone/championnat/catégorie/manche — validés au chargement CSV.
			resets.team = null;
		}
		if (key === 'zone') {
			resets.championship = null;
			resets.category = null;
			resets.team = null;
			if (value && data) {
				const zoneChampionships = getChampionships(data.teams, value, data.individuals);
				if (zoneChampionships.length === 1) {
					resets.championship = zoneChampionships[0];
				}
			}
		}
		if (key === 'championship') {
			resets.category = null;
			resets.team = null;
		}
		if (key === 'category') {
			resets.team = null;
		}
		patchParams({ [key]: value, ...resets });
	};

	const toggleTeam = (teamNumber) => {
		patchParams({ team: state.team === teamNumber ? null : teamNumber });
	};
	const resolveFavoriteContext = useCallback((row) => ({
		zone: row.zone
			|| (state.mainTab === 'competitions' && hasCompetitionContext ? state.zone : state.equipesZone)
			|| '',
		championship: row.championship
			|| (state.mainTab === 'competitions' && hasCompetitionContext ? state.championship : state.equipesCategory)
			|| '',
		category: row.category
			|| (state.mainTab === 'competitions' && hasCompetitionContext ? state.category : '')
			|| '',
	}), [
		state.mainTab,
		hasCompetitionContext,
		state.zone,
		state.championship,
		state.category,
		state.equipesZone,
		state.equipesCategory,
	]);
	const makeFavoriteId = useCallback((row) => {
		const ctx = resolveFavoriteContext(row);
		return `${state.year}::${row.teamNumber}::${ctx.championship}::${ctx.category}`;
	}, [state.year, resolveFavoriteContext]);
	const toggleFavoriteTeam = useCallback((row) => {
		const ctx = resolveFavoriteContext(row);
		const id = makeFavoriteId(row);
		setFavoriteTeams((prev) => {
			if (prev.some((fav) => fav.id === id)) {
				return prev.filter((fav) => fav.id !== id);
			}
			return [
				...prev,
				{
					id,
					year: state.year,
					teamNumber: row.teamNumber,
					teamName: row.teamName,
					club: row.club || '',
					zone: ctx.zone,
					championship: ctx.championship,
					category: ctx.category,
				},
			];
		});
	}, [makeFavoriteId, resolveFavoriteContext, state.year]);
	const openCompetitionFromCareer = useCallback((ctx, year) => {
		patchParams({
			tab: 'competitions',
			view: 'classement',
			year,
			zone: ctx.zone,
			championship: ctx.championship,
			category: ctx.category || null,
			round: null,
			team: ctx.rankKind === 'team' ? ctx.teamNumber : null,
			joueur: null,
			stats: null,
		});
	}, [patchParams]);

	const openCompetitionFromTeam = useCallback((row, { openTeam = true } = {}) => {
		patchParams({
			tab: 'competitions',
			view: 'classement',
			zone: row.zone,
			championship: row.championship,
			category: row.category || state.equipesCategory || null,
			round: null,
			team: openTeam ? row.teamNumber : null,
		});
	}, [patchParams, state.equipesCategory]);

	return (
		<NewResultsThemeProvider>
		<Box className="quilles-page new-results-page">
			<Box className="quilles-page-content">
				<header className="new-results-hero">
					<div className="new-results-hero-top">
						<div>
							<div className="new-results-hero-title-row">
								<h1>
									{heroTitle}
									<span className="new-results-badge">Nouveau</span>
								</h1>
							</div>
							<p className="new-results-hero-sub">{heroSub}</p>
						</div>
						<div className="new-results-hero-actions">
							<a
								href={OFFICIAL_TEAM_RESULTS_URL}
								className="new-results-link-classic"
								target="_blank"
								rel="noopener noreferrer"
							>
								Retour site officiel
							</a>
							<NewResultsThemeToggle />
						</div>
					</div>

					<nav className="new-results-main-tabs" aria-label="Modes de consultation">
						{MAIN_TABS.map((tab) => (
							<button
								key={tab.id}
								type="button"
								className={`new-results-main-tab ${state.mainTab === tab.id ? 'new-results-main-tab--active' : ''}`}
								onClick={() => patchParams({
									tab: tab.id,
									joueur: null,
									stats: null,
									team: null,
									...tabSwitchNavigationResets(tab.id),
								})}
							>
								{tab.label}
							</button>
						))}
					</nav>
				</header>

				{(loading || !data) && (
					<div className="new-results-loading">
						<CircularProgress />
					</div>
				)}

				{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

				{data && (
					<>
						{state.mainTab === 'competitions' && (
							<CompetitionFiltersToolbar
								state={state}
								zones={zones}
								championships={championships}
								categories={categories}
								onUpdateFilter={updateFilter}
							/>
						)}

						{sponsorBanner && state.mainTab === 'competitions' && hasCompetitionContext && (
							<div className="new-results-mobile-sponsor" aria-label="Publicité">
								<QuillesSponsorBanner banner={sponsorBanner} />
							</div>
						)}

						{state.mainTab === 'equipes' && (
							<>
								<EquipesTabToolbar
									state={state}
									zones={zones}
									equipesChampionships={equipesChampionships}
									clubs={clubs}
									onPatchParams={patchParams}
								/>
								{sponsorBanner && (
									<div className="new-results-mobile-sponsor" aria-label="Publicité">
										<QuillesSponsorBanner banner={sponsorBanner} />
									</div>
								)}
							</>
						)}

						{state.mainTab === 'joueurs' && (
							<>
								<JoueursTabToolbar
									state={state}
									localSearch={localSearch}
									onLocalSearchChange={setLocalSearch}
									playerLicenceCategories={playerLicenceCategories}
									playerLicenceTypes={playerLicenceTypes}
									playerClubs={playerClubs}
									minManchesOptions={minManchesOptions}
									effectivePlayerPage={effectivePlayerPage}
									totalPlayerPages={totalPlayerPages}
									onPatchParams={patchParams}
								/>
								{sponsorBanner && (
									<div className="new-results-mobile-sponsor" aria-label="Publicité">
										<QuillesSponsorBanner banner={sponsorBanner} />
									</div>
								)}
								<div className="new-results-panel">
									{filteredCategoryPlayers.length === 0 && (
										<p className="new-results-hint">Aucun joueur trouvé.</p>
									)}
									{filteredCategoryPlayers.length > 0 && (
										<>
											<PlayersRankingTable
												players={pagedCategoryPlayers}
												onSelectPlayer={(licence) => patchParams({ joueur: licence })}
												rankOffset={playerPageStartIndex}
												sortColumn={playerSortColumn}
												onSortColumn={setPlayerSortColumn}
											/>
										</>
									)}
								</div>
							</>
						)}

						{state.mainTab === 'carriere' && (
							<Suspense fallback={<LazyPanelFallback />}>
								<CareerTabPanel
									state={state}
									localSearch={localSearch}
									onLocalSearchChange={setLocalSearch}
									careerLoading={careerLoading}
									careerError={careerError}
									individualsByYear={individualsByYear}
									filteredCareerPlayers={filteredCareerPlayers}
									pagedCareerPlayers={pagedCareerPlayers}
									careerPlayerPageStartIndex={careerPlayerPageStartIndex}
									effectiveCareerPlayerPage={effectiveCareerPlayerPage}
									totalCareerPlayerPages={totalCareerPlayerPages}
									playerSortColumn={playerSortColumn}
									onPlayerSortColumn={setPlayerSortColumn}
									onPatchParams={patchParams}
									selectedCareer={selectedCareer}
									fullCareer={fullCareer}
									selectedCareerStats={selectedCareerStats}
									fullCareerStats={fullCareerStats}
									careerFilter={careerFilter}
									onCareerFilterChange={handleCareerFilterChange}
									sponsorBanner={sponsorBanner}
									onOpenCompetitionFromCareer={openCompetitionFromCareer}
									CareerDetailPage={PlayerCareerDetailPage}
									CareerStatsPage={PlayerCareerStatsPage}
								/>
							</Suspense>
						)}

						<PlayerStatsModal
							open={Boolean(selectedPlayer) && state.mainTab !== 'carriere'}
							player={selectedPlayer}
							filters={state.mainTab === 'competitions' && hasCompetitionContext ? filters : null}
							sponsorBanner={sponsorBanner}
							onClose={() => patchParams({ joueur: null })}
						/>

						{state.mainTab !== 'joueurs' && state.mainTab !== 'carriere' && (
						<Suspense fallback={<LazyPanelFallback />}>
							<TeamsRankingsPanel
								state={state}
								hasCompetitionContext={hasCompetitionContext}
								isIndividuelCompetition={isIndividuelCompetition}
								showSidebar={showSidebar}
								rounds={rounds}
								activeRound={activeRound}
								favoriteRows={favoriteRows}
								sortedFavoriteRows={sortedFavoriteRows}
								sortedCompetitionPlayers={sortedCompetitionPlayers}
								competitionMainPlayers={competitionMainPlayers}
								displayRows={displayRows}
								sortedDisplayRows={sortedDisplayRows}
								teamSortColumn={teamSortColumn}
								onTeamSortColumn={handleTeamSortColumn}
								playerSortColumn={playerSortColumn}
								onPlayerSortColumn={setPlayerSortColumn}
								favoriteIds={favoriteIds}
								makeFavoriteId={makeFavoriteId}
								expandedTeam={expandedTeam}
								sponsorBanner={sponsorBanner}
								topPlayers={topPlayers}
								onPatchParams={patchParams}
								onToggleTeam={toggleTeam}
								onToggleFavorite={toggleFavoriteTeam}
								onOpenTeam={openCompetitionFromTeam}
							/>
						</Suspense>
					)}
					</>
				)}
			</Box>

			<QuillesSiteFooter />
		</Box>
		</NewResultsThemeProvider>
	);
}

export default NewResultatsEquipes;

